import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { Button } from '@/components/ui/button';
import type { billing_DunningCockpitItemResponse } from '@/api/models/billing_DunningCockpitItemResponse';

export type DunningManualActionKind = 'pause' | 'resume' | 'send-now' | 'handover' | 'cancel';

interface Props {
  item: billing_DunningCockpitItemResponse;
  availableActions: string[];
  onAction: (kind: DunningManualActionKind) => void;
}

const TERMINAL_STATES = new Set(['paid', 'collections_handover', 'cancelled']);

export function DunningManualActions({ item, availableActions, onAction }: Props) {
  const { i18n } = useLingui();
  const householdName = item.household_name ?? '';
  const isPaused = !!item.is_paused;
  const isTerminal = TERMINAL_STATES.has(item.current_state ?? '');
  const isSuppressed = !!item.is_suppressed;
  // Gate every button on both FSM-allowed transitions and the human-friendly
  // invariants the spec lists, so the UI never offers an action the backend
  // would reject with a 409.
  const canPause = !isPaused && !isTerminal && availableActions.includes('pause');
  const canResume = isPaused && availableActions.includes('resume');
  const canSendNow =
    !isTerminal &&
    !isPaused &&
    !isSuppressed &&
    availableActions.some((a) => a.startsWith('send_'));
  const canHandover = !isTerminal && availableActions.includes('mark_handover');
  const canCancel = !isTerminal && availableActions.includes('cancel');

  return (
    <div className="flex flex-wrap gap-2" data-testid="dunning-manual-actions">
      <Button
        variant="outline"
        disabled={!canPause}
        onClick={() => onAction('pause')}
        data-testid="action-pause"
        aria-label={i18n._(
          msg({ id: 'dunning.action.pause.aria', message: 'Pause dunning for {householdName}' }),
          { householdName },
        )}
      >
        <Trans>Pause</Trans>
      </Button>
      <Button
        variant="outline"
        disabled={!canResume}
        onClick={() => onAction('resume')}
        data-testid="action-resume"
      >
        <Trans>Resume</Trans>
      </Button>
      <Button
        variant="outline"
        disabled={!canSendNow}
        onClick={() => onAction('send-now')}
        data-testid="action-send-now"
        aria-label={i18n._(
          msg({ id: 'dunning.action.sendNow.aria', message: 'Send next reminder now for {householdName}' }),
          { householdName },
        )}
      >
        <Trans>Send next reminder now</Trans>
      </Button>
      <Button
        variant="outline"
        disabled={!canHandover}
        onClick={() => onAction('handover')}
        data-testid="action-handover"
      >
        <Trans>Mark Collections Handover</Trans>
      </Button>
      <Button
        variant="destructive"
        disabled={!canCancel}
        onClick={() => onAction('cancel')}
        data-testid="action-cancel"
      >
        <Trans>Cancel</Trans>
      </Button>
    </div>
  );
}
